import { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { ENTITY_MODAL_TYPES } from './entity-modal.constants';
import { useTranslation } from 'react-i18next';
import {
  FieldLabel,
  ModalInput,
  ModalTextarea,
  CustomSelect,
  Pill,
  Section,
  FieldCard,
  TagList,
} from './EntityModalComponents';
import type { EntityFormProps } from './entity-modal.types';

const accent = ENTITY_MODAL_TYPES.event.accent;

const EVENT_TYPE_PILLS = [
  { value: 'meeting', label: 'entity.event_type_meeting' },
  { value: 'reminder', label: 'entity.event_type_reminder' },
  { value: 'focus', label: 'entity.event_type_work_session' },
] as const;

export function EventForm({
  isEdit,
  formData,
  onChange,
  domains,
  goals,
  tags,
  onAddTag,
  onRemoveTag,
}: EntityFormProps) {
  const { t } = useTranslation();
  const domainOptions = domains.map((d) => ({ value: d.id, label: d.name }));
  const goalOptions = [
    { value: '', label: t('common.none_m') },
    ...goals.map((g) => ({ value: g.id, label: g.title })),
  ];

  const countdown = useMemo(() => {
    if (!isEdit || !formData.date) return null;
    const target = new Date(`${formData.date}T${formData.time || '00:00'}:00`);
    const diff = target.getTime() - Date.now();
    if (diff < 0) return { label: t('entity.event_past'), color: '#71717a' };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    if (days > 0) {
      const color = days <= 3 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#22c55e';
      return {
        label: t(days > 1 ? 'entity.event_countdown_days_plural' : 'entity.event_countdown_days', {
          days,
        }),
        color,
      };
    }
    return { label: t('entity.event_countdown_hours', { hours }), color: '#ef4444' };
  }, [isEdit, formData.date, formData.time]);

  return (
    <div className="flex flex-col gap-4">
      <input
        type="text"
        required
        placeholder={t('entity.event_name_placeholder')}
        value={formData.title || ''}
        onChange={(e) => onChange('title', e.target.value)}
        className="w-full bg-transparent border-none text-lg font-semibold text-zinc-100 outline-none placeholder:text-zinc-600 tracking-tight"
      />

      {countdown && (
        <div
          className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-lg"
          style={{ background: `${countdown.color}14`, border: `1px solid ${countdown.color}30` }}
        >
          <Clock className="w-3 h-3" style={{ color: countdown.color }} />
          <span className="text-xs font-semibold" style={{ color: countdown.color }}>
            {countdown.label}
          </span>
        </div>
      )}

      <FieldCard accent={accent}>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <FieldLabel>{t('entity.event_start_date')}</FieldLabel>
            <ModalInput
              type="date"
              value={formData.date || ''}
              onChange={(val) => onChange('date', val)}
              min={!isEdit ? new Date().toISOString().split('T')[0] : undefined}
            />
          </div>
          <div>
            <FieldLabel>{t('entity.event_start_time')}</FieldLabel>
            <ModalInput
              type="time"
              value={formData.time || ''}
              onChange={(val) => onChange('time', val)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5 mt-2.5">
          <div>
            <FieldLabel>{t('entity.event_end_date')}</FieldLabel>
            <ModalInput
              type="date"
              value={formData.endDate ?? ''}
              onChange={(val) => onChange('endDate', val)}
            />
          </div>
          <div>
            <FieldLabel>{t('entity.event_end_time')}</FieldLabel>
            <ModalInput
              type="time"
              value={formData.endTime ?? ''}
              onChange={(val) => onChange('endTime', val)}
            />
          </div>
        </div>
      </FieldCard>

      <div className="flex gap-1.5">
        {EVENT_TYPE_PILLS.map((ep) => (
          <Pill
            key={ep.value}
            label={t(ep.label)}
            color={accent}
            active={(formData.type || 'meeting') === ep.value}
            onClick={() => onChange('type', ep.value)}
          />
        ))}
      </div>

      <Section title={t('common.details')} defaultOpen accent={accent}>
        <div className="flex flex-col gap-3.5">
          <div>
            <FieldLabel>{t('common.domain')}</FieldLabel>
            <CustomSelect
              options={domainOptions}
              value={formData.domainId || ''}
              onChange={(val) => onChange('domainId', val)}
            />
          </div>
          <div>
            <FieldLabel>{t('entity.event_location')}</FieldLabel>
            <ModalInput
              placeholder={t('entity.event_location_placeholder')}
              value={formData.location ?? ''}
              onChange={(val) => onChange('location', val)}
            />
          </div>
          <div>
            <FieldLabel>{t('common.description')}</FieldLabel>
            <ModalTextarea
              placeholder={t('entity.event_description_placeholder')}
              value={formData.description || ''}
              onChange={(val) => onChange('description', val)}
            />
          </div>
        </div>
      </Section>

      <Section title={t('common.attachments')} defaultOpen={isEdit} accent={accent}>
        <div className="flex flex-col gap-2.5">
          <div>
            <FieldLabel>{t('entity.event_linked_goal')}</FieldLabel>
            <CustomSelect
              options={goalOptions}
              value={formData.goalId || ''}
              onChange={(val) => onChange('goalId', val || null)}
            />
          </div>
        </div>
      </Section>

      <Section title="Tags" defaultOpen={false} accent={accent}>
        <TagList tags={tags} accent={accent} onAdd={onAddTag} onRemove={onRemoveTag} />
      </Section>

      <div>
        <FieldLabel>{t('common.budget')}</FieldLabel>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder={t('entity.event_budget_placeholder')}
            value={formData.budget ?? ''}
            onChange={(e) =>
              onChange('budget', e.target.value === '' ? null : parseFloat(e.target.value))
            }
            className="w-full bg-zinc-900/40 border border-zinc-600/45 rounded-[10px] px-3 py-2.5 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:border-violet-500/50 focus:bg-zinc-900/60 outline-none transition"
          />
          <span className="text-sm text-zinc-500">€</span>
        </div>
      </div>
    </div>
  );
}
